import type { z } from '@awpmarine/core/zod';
import {
  type AnyFormApi,
  type FormAsyncValidateOrFn,
  type FormValidateOrFn,
  type StandardSchemaV1,
  createFormHook,
  createFormHookContexts,
  revalidateLogic,
} from '@tanstack/react-form';

// The shared plumbing of the apps' forms. Each app builds its form hooks with `createAppForm`, passing its own field
// and form components, which read the field and form through the contexts below. A form is built with `useSchemaForm`
// from its zod schema, so errors show from the first submit on; a form without a schema, or one that validates on
// every change, uses `useAppForm` directly.

export { useSelector } from '@tanstack/react-form';

export const { fieldContext, formContext, useFieldContext, useFormContext } = createFormHookContexts();

/**
 * What a form starts from: any field may be missing or empty until it is filled in. A misspelled key is still caught;
 * a required field left empty is the schema's to reject on submit.
 */
type FormDefaults<T> = T extends Date | Blob | readonly unknown[]
  ? T | null
  : T extends object
    ? { [K in keyof T]?: FormDefaults<T[K]> | null }
    : T | null;

// `createFormHook` constrains its components this way; anything narrower than `any` rejects components with props.
/* oxlint-disable typescript/no-explicit-any */
export function createAppForm<
  TFieldComponents extends Record<string, React.ComponentType<any>>,
  TFormComponents extends Record<string, React.ComponentType<any>>,
>({ fieldComponents, formComponents }: { fieldComponents: TFieldComponents; formComponents: TFormComponents }) {
  /* oxlint-enable typescript/no-explicit-any */
  const { useAppForm, withForm, withFieldGroup } = createFormHook({
    fieldContext,
    formContext,
    fieldComponents,
    formComponents,
  });

  /**
   * `useAppForm` for a zod schema: it validates on submit, then on every change, and `onSubmit` and `onSubmitAsync`
   * receive the schema's output, so trims and other transforms reach the API. The form itself still holds what was
   * typed. A string `onSubmitAsync` returns is shown by the app's error component; `true` blocks the submit without
   * a message.
   */
  function useSchemaForm<TSchema extends z.ZodType, TSubmitMeta = unknown, TSubmitError = never>({
    schema,
    defaultValues,
    onSubmit,
    onSubmitAsync,
    onSubmitMeta,
  }: {
    schema: TSchema;
    defaultValues: FormDefaults<z.input<TSchema>>;
    onSubmit?: (props: { value: z.output<TSchema>; formApi: AnyFormApi; meta: TSubmitMeta }) => unknown;
    onSubmitAsync?: (props: { value: z.output<TSchema>; formApi: AnyFormApi }) => Promise<TSubmitError | undefined>;
    onSubmitMeta?: TSubmitMeta;
  }) {
    type Values = z.input<TSchema>;
    type Unset = FormValidateOrFn<Values> | undefined;
    type UnsetAsync = FormAsyncValidateOrFn<Values> | undefined;
    // Unset when a form has none, as `useAppForm` would infer, so `withForm` pieces still accept the form.
    type SubmitAsync = [TSubmitError] extends [never]
      ? UnsetAsync
      : (props: { value: Values; formApi: AnyFormApi }) => Promise<TSubmitError | undefined>;
    // The validator types are spelled out because TypeScript cannot infer them through the generic schema.
    return useAppForm<
      Values,
      Unset,
      Unset,
      UnsetAsync,
      Unset,
      UnsetAsync,
      Unset,
      SubmitAsync,
      StandardSchemaV1<Values, unknown>,
      UnsetAsync,
      UnsetAsync,
      TSubmitMeta
    >({
      defaultValues: defaultValues as Values,
      validationLogic: revalidateLogic(),
      validators: {
        // A zod schema is a Standard Schema of its own input.
        onDynamic: schema as unknown as StandardSchemaV1<Values, unknown>,
        // Runs only once the schema has passed, so parsing cannot throw.
        onSubmitAsync: (onSubmitAsync &&
          (({ value, formApi }: { value: Values; formApi: AnyFormApi }) =>
            onSubmitAsync({ value: schema.parse(value), formApi }))) as SubmitAsync,
      },
      onSubmitMeta,
      onSubmit: ({ value, formApi, meta }) => onSubmit?.({ value: schema.parse(value), formApi, meta }),
    });
  }

  return { useAppForm, withForm, withFieldGroup, useSchemaForm };
}

export function submitWith(form: { handleSubmit: () => Promise<void> }) {
  return (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    // React bubbles a submit through portals, so a form in a dialog would also submit the form that opened it.
    event.stopPropagation();
    void form.handleSubmit();
  };
}
